import { forwardRef, useMemo, useRef } from 'react';
import cx from 'classnames';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { InvisibleText } from '../InvisibleText';
import { Flow } from '../Layouts/Flow';
import { useTilesContext } from '../Tile';
import { TileContainer, TileHeader, useInteractiveTile, useTileKeyboardTooltip } from '../Tile/__internal__';
import bundle from './../Tile/NovaComponents-Tile-gui-icu.properties';
import cn from './PresentationTile.css.json';
import './PresentationTile.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useHtmlId = createUseHtmlId('Presentation-Tile');
const useTextLocalizer = createUseTextLocalizer(bundle);
export const PresentationTile = /*#__PURE__*/forwardRef(function PresentationTile({
  style,
  className,
  body,
  testId,
  role = 'group',
  tabIndex,
  title,
  subTitle,
  description,
  icon,
  avatarProps,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  objectIconAriaHidden,
  ...rest
}, ref) {
  const tileRef = useRef(null);
  const dt = useDesignTokens();
  const setKeyboardTooltipRef = useTileKeyboardTooltip(rest);
  const bodyRef = useRef(null);
  const customContentRefs = useMemo(() => [bodyRef], []);
  const localize = useTextLocalizer();
  const tilesContext = useTilesContext();
  const [titleId, defaultHeaderIconId, interactiveModeAnnouncementId] = useHtmlId(3);
  const headerIconId = icon?.id ?? defaultHeaderIconId;
  const predefinedFocusableElements = {
    title,
    icon,
    subTitle,
    description
  };
  const hasNoFocusableElements = !hasFocusableElements(predefinedFocusableElements);

  // F2 support is disabled by default
  // consumer has to explicitly set disableInteractiveMode to false to use F2
  const disableInteractiveMode = rest.disableInteractiveMode ?? tilesContext?.disableInteractiveMode ?? true;

  // when interactive mode is enabled, Tiles can have two modes: browse and interactive.
  // User can toggle tile between browse and interactive using F2.  If not interactive, the tile itself
  // can still receive keyboard focus. Default is to start in browse mode.
  const {
    isInteractive,
    ...interactiveHandlers
  } = useInteractiveTile({
    tileRef,
    customContentRefs,
    disableInteractiveMode,
    suppressCustomContentFocus: tabIndex === 0 || tabIndex === undefined && hasNoFocusableElements
  });
  const containerTabIndex = disableInteractiveMode && tabIndex !== undefined ? tabIndex : disableInteractiveMode && hasNoFocusableElements || !disableInteractiveMode && !isInteractive ? 0 : -1;
  return /*#__PURE__*/_jsxs(TileContainer, {
    ...rest,
    testId: testId,
    disableClickDelay: true // there is no click handler, this is set for performance reasons
    ,
    role: role,
    className: cx(className, cn.tile),
    tabIndex: containerTabIndex,
    style: {
      ...style,
      ...renderInlineCssVars({
        pd: dt.tokens['space-base-m']
      }, 'presentation-tile')
    },
    ref: element => {
      setRef(ref, element);
      setRef(tileRef, element);
      setKeyboardTooltipRef(element);
    },
    isInteractive: isInteractive,
    ariaLabel: ariaLabel,
    ariaLabelledBy: ariaLabelledBy ?? (ariaLabel ? undefined : [...(title ? [titleId] : []), ...(icon && !objectIconAriaHidden ? [headerIconId] : [])].join(' ')),
    ariaDescribedBy: ariaDescribedBy ?? [...(!tilesContext?.suppressInteractiveModeAnnouncement && disableInteractiveMode !== true ? [interactiveModeAnnouncementId] : [])].join(' '),
    disableInteractiveMode: disableInteractiveMode,
    ...(disableInteractiveMode ? {} : interactiveHandlers),
    children: [/*#__PURE__*/_jsx(InvisibleText, {
      id: interactiveModeAnnouncementId,
      children: localize('interactiveMode.hint.keyboardTooltip.label')
    }), /*#__PURE__*/_jsxs(Flow, {
      orientation: "vertical",
      spacing: "s",
      children: [/*#__PURE__*/_jsx(TileHeader, {
        ...rest,
        titleId: titleId,
        title: title,
        subTitle: subTitle,
        description: description,
        icon: icon ? {
          ...icon,
          id: headerIconId
        } : undefined,
        avatarProps: avatarProps,
        objectIconAriaHidden: objectIconAriaHidden,
        tabIndex: containerTabIndex === 0 ? -1 : 0,
        testId: postfixTestId(testId, 'header')
      }), body ? /*#__PURE__*/_jsx("div", {
        ref: bodyRef,
        "data-testid": postfixTestId(testId, 'body'),
        children: body
      }) : null]
    })]
  });
});
const hasFocusableElements = props => {
  const focusableElements = props.objectIconAriaHidden ? ['description', 'title', 'subTitle'] : ['description', 'title', 'subTitle', 'icon'];
  return !!Object.entries(props).filter(prop => focusableElements.includes(prop[0]) && prop[1] !== undefined).length;
};
//# sourceMappingURL=PresentationTile.js.map